// hooks/useViewAsCustomer.ts
/**
 * A superadmin's per-viewer "View as customer" switch (ADR-0063 successor,
 * plan 2026-10-23-superadmin-sees-sections-hidden-from-the-customer §1).
 *
 * With it on, `useSectionAccess` drops the superadmin view and returns exactly
 * what the account's members see, so support can reproduce the customer's
 * navigation. A per-viewer convenience, so it lives in localStorage; every
 * access is wrapped because storage can throw (private window, blocked site
 * data) and the switch must then simply read as off.
 */

import { useSyncExternalStore } from "react";

const STORAGE_KEY = "superadmin-view-as-customer";
const listeners = new Set<() => void>();

function read(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

let current = read();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setViewAsCustomer(on: boolean): void {
  current = on;
  try {
    if (on) localStorage.setItem(STORAGE_KEY, "true");
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable: the switch still works for this page load.
  }
  listeners.forEach((l) => l());
}

export function useViewAsCustomer(): boolean {
  return useSyncExternalStore(subscribe, () => current, () => false);
}
